import { useCallback, useRef } from 'react';
import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { extractApiErrorMessage } from '@frontend/api/http';
import { useInstanceId } from '@frontend/hooks';
import { useSocket, useSocketChannel, useSocketReconnect } from '@frontend/hooks/useSocket';
import {
  AgentExecutionResponse,
  MemoryChunk,
  MemoryChunkList,
  SpecializedAgent,
  SpecializedAgentExecution,
  SpecializedAgentListItem,
  SpecializedAgentSocketMessage,
} from '@frontend/types';
import { sendSentryError } from '@frontend/utils/sendSentryError';

import {
  checkAgentIdAvailability,
  createSpecializedAgent,
  deleteSpecializedAgent,
  executeSpecializedAgent,
  getAgentMemoryChunks,
  getSpecializedAgent,
  getSpecializedAgentExecution,
  getSpecializedAgentKey,
  getSpecializedAgents,
  getSpecializedAgentVersion,
  stopSpecializedAgentExecution,
  stopWaitingSpecializedAgentExecution,
  updateSpecializedAgent,
} from '../services/specialized-agent';

function handleApiErrorWithSentry(
  error: unknown,
  fallbackMessage: string,
  context?: Record<string, unknown>
): string {
  const errorMessage = extractApiErrorMessage(error);

  if (!errorMessage) {
    sendSentryError(`${fallbackMessage}: No error message from API`, {
      originalError: error,
      ...context,
    });
  }

  return errorMessage || fallbackMessage;
}

export function useGetSpecializedAgentsList() {
  const { instanceId } = useInstanceId();
  const {
    data: agents,
    isPending,
    error,
  } = useQuery<SpecializedAgentListItem[]>({
    queryKey: getSpecializedAgents.key(instanceId ?? '', 1, 1000, false),
    queryFn: getSpecializedAgents({ instanceId: instanceId ?? '' }),
    enabled: !!instanceId,
  });

  return { agents: agents ?? [], isPending, error };
}

export function useGetSpecializedAgent(instanceId: string, agentId: string) {
  const url =
    instanceId && agentId ? `/hypatia/api/v1/${instanceId}/agents/specialized/${agentId}` : '';
  const {
    data: agent,
    isLoading,
    error,
  } = useQuery<SpecializedAgent>({
    queryKey: getSpecializedAgentKey(agentId),
    queryFn: getSpecializedAgent(url),
    enabled: !!instanceId && !!agentId,
  });

  return { agent: agent ?? undefined, isLoading, error };
}

export function useGetSpecializedAgentVersion(versionUrl: string) {
  const {
    data: version,
    isLoading,
    error,
  } = useQuery<SpecializedAgent>({
    queryKey: getSpecializedAgentVersion.key(versionUrl),
    queryFn: getSpecializedAgentVersion(versionUrl),
    enabled: !!versionUrl,
  });

  return { version: version ?? undefined, isLoading, error };
}

export function useDeleteSpecializedAgent(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const silentRef = useRef(false);
  const { mutate, mutateAsync, isPending, error } = useMutation({
    mutationFn: async (deleteUrl: string) => {
      return deleteSpecializedAgent(deleteUrl)();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agents'] });
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      if (!silentRef.current) toaster.create('Agent deleted successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      if (!silentRef.current) toaster.create('Failed to delete agent', { type: 'danger' });
    },
  });
  return { mutate, mutateAsync, isPending, error, silentRef };
}

export function useUpdateSpecializedAgent(
  instanceId: string,
  agentId: string,
  onSuccess?: () => void
) {
  const queryClient = useQueryClient();
  const url =
    instanceId && agentId ? `/hypatia/api/v1/${instanceId}/agents/specialized/${agentId}` : '';
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (agent: SpecializedAgent) => {
      return updateSpecializedAgent(url, agent)();
    },
    onSuccess: (_, agent) => {
      if (instanceId) {
        queryClient.invalidateQueries({
          queryKey: getSpecializedAgents.key(instanceId),
        });
        if (agent.id) {
          queryClient.invalidateQueries({
            predicate: query =>
              Array.isArray(query.queryKey) &&
              query.queryKey.includes('opal:specialized-agent-versions') &&
              query.queryKey.includes(agent.id),
          });
        }
      }
      queryClient.invalidateQueries({ queryKey: getSpecializedAgentKey(agentId) });
      toaster.create('Agent updated successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      console.error('Error updating specialized agent:', error);
      const errorMessage = handleApiErrorWithSentry(error, 'Failed to update agent', { url });
      toaster.create(errorMessage, { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useCreateSpecializedAgent(
  instanceId: string,
  onSuccess?: (createdAgent: SpecializedAgent) => void
) {
  const { mutate, isPending, error } = useMutation({
    mutationFn: async (agent: SpecializedAgent) => {
      const response = await createSpecializedAgent(agent, instanceId)();
      return response.data;
    },
    onSuccess: (createdAgent: SpecializedAgent) => {
      toaster.create('Agent created successfully', { type: 'success' });
      onSuccess?.(createdAgent);
    },
    onError: (error: unknown) => {
      console.error('Error creating specialized agent:', error);
      const errorMessage = handleApiErrorWithSentry(error, 'Failed to create agent', {
        instanceId,
      });
      toaster.create(errorMessage, { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export function useCheckAgentIdAvailability() {
  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async ({ agentId, instanceId }: { agentId: string; instanceId: string }) => {
      const result = await checkAgentIdAvailability(agentId, instanceId)();
      return { ...result, lastCheckedId: agentId };
    },
  });
  return { mutate, isPending, error, data };
}

export function useExecuteSpecializedAgent({
  onSuccess,
  onError,
}: {
  onSuccess?: (data: AgentExecutionResponse, etag?: string) => void;
  onError?: () => void;
}) {
  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async ({
      executeUrl,
      parameters,
      runInBackground = true,
    }: {
      executeUrl: string;
      parameters: Record<string, unknown>;
      runInBackground?: boolean;
    }) => {
      return executeSpecializedAgent(executeUrl, parameters, runInBackground)();
    },
    onSuccess: result => {
      onSuccess?.(result.data, result.etag);
    },
    onError: () => {
      onError?.();
    },
  });
  return { mutate, isPending, error, data };
}

export function useGetSpecializedAgentExecution(
  instanceId: string,
  executionId: string,
  onExecutionCompleted?: () => void,
  onExecutionFailed?: () => void
) {
  const { connected: socketConnected } = useSocket();
  const {
    data: execution,
    isPending,
    error,
    refetch,
  } = useQuery<SpecializedAgentExecution>({
    queryKey: ['opal:specialized-agent-execution', instanceId, executionId],
    queryFn: getSpecializedAgentExecution(instanceId, executionId),
    enabled: !!instanceId && !!executionId,
    placeholderData: previousData => previousData,
    refetchInterval: query => {
      const status = query.state?.data?.status?.toLowerCase();
      const isStreaming = status ? status !== 'completed' && status !== 'failed' : false;
      const shouldPoll = !socketConnected && isStreaming;
      return shouldPoll ? 2000 : false;
    },
  });
  const queryClient = useQueryClient();
  const memoryId = execution?.memory_id;

  const handleSocketUpdate = useCallback(
    (message: SpecializedAgentSocketMessage) => {
      if (message.type === 'started' || message.type === 'completion') {
        queryClient.invalidateQueries({
          queryKey: ['opal:specialized-agent-execution', instanceId, executionId],
        });
      }
      if (message.type === 'completion') {
        if (message.data.status === 'completed') {
          onExecutionCompleted?.();
        } else if (message.data.status === 'failed') {
          onExecutionFailed?.();
        }
      }
    },
    [instanceId, executionId, queryClient, onExecutionCompleted, onExecutionFailed]
  );

  const handleMemorySocketUpdate = useCallback(
    (message: SpecializedAgentSocketMessage) => {
      if (message.type === 'memory_chunk') {
        queryClient.invalidateQueries({
          queryKey: ['opal:specialized-agent-execution', instanceId, executionId],
        });
      }
    },
    [instanceId, executionId, queryClient]
  );

  useSocketChannel(
    executionId ? `specialized_agent_execution:${executionId}` : null,
    handleSocketUpdate
  );
  useSocketChannel(memoryId ? `episodic_memory:${memoryId}` : null, handleMemorySocketUpdate);

  // Backfill anything missed while the socket was dropped: root-namespace
  // events are fire-once with no replay, so a reconnect needs an explicit
  // refetch (otherwise the user must refresh to see it).
  useSocketReconnect(() => {
    queryClient.invalidateQueries({
      queryKey: ['opal:specialized-agent-execution', instanceId, executionId],
    });
  });

  return { execution, isPending, error, refetch };
}

export function useGetAgentMemoryChunks(
  memoryId?: string,
  memoryUrl?: string,
  isStreaming: boolean = false
) {
  const { connected: socketConnected } = useSocket();
  const shouldPoll = !socketConnected && isStreaming;

  const {
    data: memoryChunks,
    isPending,
    error,
  } = useQuery<MemoryChunkList>({
    queryKey: ['opal:agent-memory-chunks', memoryUrl],
    queryFn: () => {
      if (!memoryUrl) throw new Error('memoryUrl is required');
      return getAgentMemoryChunks(memoryUrl)();
    },
    enabled: !!memoryUrl,
    refetchInterval: shouldPoll ? 2000 : false,
    placeholderData: previousData => previousData,
  });

  const queryClient = useQueryClient();

  const handleSocketUpdate = useCallback(
    (message: SpecializedAgentSocketMessage) => {
      if (message.type === 'memory_chunk') {
        queryClient.setQueryData<MemoryChunkList>(['opal:agent-memory-chunks', memoryUrl], old =>
          mergeBySequence(old ?? [], message.data.chunks)
        );
      }
    },
    [memoryUrl, queryClient]
  );

  useSocketChannel(memoryId ? `episodic_memory:${memoryId}` : null, handleSocketUpdate);

  // Root-namespace chunk events are fire-once with no replay; refetch on
  // reconnect to recover chunks emitted while the socket was dropped.
  useSocketReconnect(() => {
    queryClient.invalidateQueries({ queryKey: ['opal:agent-memory-chunks', memoryUrl] });
  });

  return { memoryChunks: memoryChunks ?? [], isPending, error };
}

function mergeBySequence(existing: MemoryChunkList, incoming: MemoryChunkList): MemoryChunkList {
  if (!incoming?.length) return existing ?? [];
  const map = new Map<number, MemoryChunk>();
  for (const chunk of existing ?? []) map.set(chunk.sequence_number, chunk);
  for (const chunk of incoming) map.set(chunk.sequence_number, chunk);
  return Array.from(map.values()).sort((a, b) => a.sequence_number - b.sequence_number);
}

export function useStopSpecializedAgentExecution(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      instanceId,
      executionId,
      errorMessage,
    }: {
      instanceId: string;
      executionId: string;
      errorMessage: string;
    }) => {
      return stopSpecializedAgentExecution(instanceId, executionId, errorMessage)();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opal:specialized-agent-execution', variables.instanceId, variables.executionId],
      });
      toaster.create('Execution stopped successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to stop execution', { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}

export { useResumeSpecializedAgentExecution } from './useResumeSpecializedAgentExecution';

export function useStopWaitingSpecializedAgentExecution(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({
      instanceId,
      executionId,
    }: {
      instanceId: string;
      executionId: string;
    }) => {
      return stopWaitingSpecializedAgentExecution(instanceId, executionId)();
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['opal:specialized-agent-execution', variables.instanceId, variables.executionId],
      });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to stop waiting for tool', { type: 'danger' });
    },
  });
  return { mutate, isPending, error };
}
